import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Plus, PencilSimple, Trash, Key, UsersThree, CircleNotch } from "@phosphor-icons/react";

const EMPTY_USER = { name: "", lastName: "", rut: "", email: "", phone: "", cargo: "", permissions: [], password: "" };

export function CompanyUsers({ permsKey = "carrierPermissions", isNatural = false }) {
  const [users, setUsers] = useState(null);
  const [perms, setPerms] = useState([]);
  const [modal, setModal] = useState(null);
  const [pwModal, setPwModal] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/company/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
  useEffect(() => { load(); api.get("/meta/config").then((r) => setPerms(r.data[permsKey] || [])).catch(() => {}); }, [permsKey]);

  const openCreate = () => setModal({ mode: "create", data: { ...EMPTY_USER } });
  const openEdit = (u) => setModal({ mode: "edit", data: { ...EMPTY_USER, ...u, password: "" } });

  const togglePerm = (key) => setModal((m) => {
    const has = m.data.permissions.includes(key);
    return { ...m, data: { ...m.data, permissions: has ? m.data.permissions.filter((p) => p !== key) : [...m.data.permissions, key] } };
  });

  const saveUser = async () => {
    const d = modal.data;
    if (!d.name.trim()) return toast.error("Ingresa el nombre");
    if (!d.email.trim()) return toast.error("Ingresa el email");
    setBusy(true);
    try {
      if (modal.mode === "create") {
        if (!d.password || d.password.length < 6) { setBusy(false); return toast.error("La contraseña debe tener al menos 6 caracteres"); }
        await api.post("/company/users", d);
      } else {
        await api.put(`/company/users/${d.id}`, { name: d.name, lastName: d.lastName, rut: d.rut, phone: d.phone, cargo: d.cargo, permissions: d.permissions });
      }
      toast.success("Usuario guardado");
      setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setBusy(false);
  };

  const resetPassword = async () => {
    if (!pwModal.password || pwModal.password.length < 6) return toast.error("La contraseña debe tener al menos 6 caracteres");
    setBusy(true);
    try {
      await api.post(`/company/users/${pwModal.user.id}/reset-password`, { password: pwModal.password });
      toast.success("Contraseña actualizada");
      setPwModal(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setBusy(false);
  };

  const remove = async (u) => {
    if (!window.confirm(`¿Eliminar al usuario ${u.name}?`)) return;
    try { await api.delete(`/company/users/${u.id}`); toast.success("Usuario eliminado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const permLabel = (key) => (perms.find((p) => p.key === key) || {}).label || key;

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-5 mt-4" data-testid="company-users">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-3">
        <h2 className="font-display text-xl font-bold tracking-tight flex items-center gap-2"><UsersThree size={22} className="text-[#FF5A36]" /> Usuarios de la empresa</h2>
        <button onClick={openCreate} data-testid="add-company-user"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#FF5A36] text-white text-sm font-bold hover:bg-[#E04B2A] active:scale-95">
          <Plus size={16} weight="bold" /> Agregar usuario
        </button>
      </div>
      <p className="text-sm text-zinc-500 mb-4">
        {isNatural
          ? "Si eres persona natural, la empresa puede ser solo tú, o puedes sumar usuarios con acceso."
          : "Agrega usuarios con acceso propio a Frets y define qué secciones puede ver cada uno."}
      </p>

      {!users ? (
        <div className="py-8 flex justify-center"><CircleNotch size={28} className="animate-spin text-[#FF5A36]" /></div>
      ) : users.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-300 p-8 text-center text-zinc-500" data-testid="company-users-empty">
          Aún no has agregado usuarios a la empresa.
        </div>
      ) : (
        <div className="rounded-xl border border-zinc-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-zinc-400 bg-zinc-50 border-b border-zinc-200">
                <th className="px-4 py-3 font-bold">Nombre</th>
                <th className="px-4 py-3 font-bold">Cargo</th>
                <th className="px-4 py-3 font-bold">Email</th>
                <th className="px-4 py-3 font-bold">Permisos</th>
                <th className="px-4 py-3 font-bold text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-zinc-100 hover:bg-zinc-50/60" data-testid={`company-user-row-${u.id}`}>
                  <td className="px-4 py-3 font-semibold whitespace-nowrap">{u.name} {u.lastName}</td>
                  <td className="px-4 py-3 text-zinc-600 whitespace-nowrap">{u.cargo || "—"}</td>
                  <td className="px-4 py-3 text-zinc-600 whitespace-nowrap">{u.email}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(u.permissions || []).length === 0 ? <span className="text-zinc-400">Sin permisos</span>
                        : (u.permissions || []).map((p) => <span key={p} className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-700">{permLabel(p)}</span>)}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`} title="Editar" className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-zinc-100 text-zinc-600 active:scale-95"><PencilSimple size={17} /></button>
                      <button onClick={() => setPwModal({ user: u, password: "" })} data-testid={`reset-pw-${u.id}`} title="Formatear clave" className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-zinc-100 text-zinc-600 active:scale-95"><Key size={17} /></button>
                      <button onClick={() => remove(u)} data-testid={`delete-user-${u.id}`} title="Eliminar" className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-red-50 text-red-500 active:scale-95"><Trash size={17} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!modal} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="max-w-lg" data-testid="company-user-dialog">
          <DialogHeader><DialogTitle>{modal?.mode === "create" ? "Nuevo usuario" : "Editar usuario"}</DialogTitle>
            <DialogDescription>Define los datos de acceso y los permisos del usuario de la empresa.</DialogDescription>
          </DialogHeader>
          {modal && (
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              <div className="grid sm:grid-cols-2 gap-3">
                <MField label="Nombre" testid="user-name" value={modal.data.name} onChange={(v) => setModal({ ...modal, data: { ...modal.data, name: v } })} />
                <MField label="Apellido" testid="user-lastname" value={modal.data.lastName} onChange={(v) => setModal({ ...modal, data: { ...modal.data, lastName: v } })} />
                <MField label="RUT" testid="user-rut" value={modal.data.rut} onChange={(v) => setModal({ ...modal, data: { ...modal.data, rut: v } })} placeholder="11.111.111-1" />
                <MField label="Cargo / rol" testid="user-cargo" value={modal.data.cargo} onChange={(v) => setModal({ ...modal, data: { ...modal.data, cargo: v } })} placeholder="Operaciones" />
                <MField label="Teléfono" testid="user-phone" value={modal.data.phone} onChange={(v) => setModal({ ...modal, data: { ...modal.data, phone: v } })} />
                <MField label="Email" testid="user-email" value={modal.data.email} disabled={modal.mode === "edit"} onChange={(v) => setModal({ ...modal, data: { ...modal.data, email: v } })} />
              </div>
              {modal.mode === "create" && (
                <MField label="Contraseña (mín. 6)" testid="user-password" type="password" value={modal.data.password} onChange={(v) => setModal({ ...modal, data: { ...modal.data, password: v } })} />
              )}
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Permisos (qué puede ver)</label>
                <div className="grid sm:grid-cols-2 gap-2 mt-2">
                  {perms.map((p) => (
                    <label key={p.key} className="flex items-center gap-2 text-sm cursor-pointer p-2 rounded-lg border border-zinc-200 hover:border-[#FF5A36]" data-testid={`perm-${p.key}`}>
                      <input type="checkbox" checked={modal.data.permissions.includes(p.key)} onChange={() => togglePerm(p.key)} className="accent-[#FF5A36] w-4 h-4" />
                      {p.label}
                    </label>
                  ))}
                </div>
              </div>
              <button onClick={saveUser} disabled={busy} data-testid="save-company-user"
                className="w-full py-3 rounded-xl bg-[#FF5A36] text-white font-bold hover:bg-[#E04B2A] disabled:opacity-60 active:scale-95">
                {busy ? "Guardando…" : "Guardar usuario"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwModal} onOpenChange={(o) => !o && setPwModal(null)}>
        <DialogContent className="max-w-sm" data-testid="reset-password-dialog">
          <DialogHeader><DialogTitle>Formatear clave</DialogTitle>
            <DialogDescription>Establece una nueva contraseña para este usuario.</DialogDescription>
          </DialogHeader>
          {pwModal && (
            <div className="space-y-3">
              <p className="text-sm text-zinc-500">Define una nueva contraseña para <span className="font-semibold text-zinc-800">{pwModal.user.name} {pwModal.user.lastName}</span>.</p>
              <MField label="Nueva contraseña (mín. 6)" testid="new-password" type="password" value={pwModal.password} onChange={(v) => setPwModal({ ...pwModal, password: v })} />
              <button onClick={resetPassword} disabled={busy} data-testid="save-new-password"
                className="w-full py-3 rounded-xl bg-[#0F1115] text-white font-bold hover:bg-[#1A1D24] disabled:opacity-60 active:scale-95">
                {busy ? "Guardando…" : "Actualizar contraseña"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MField({ label, value, onChange, placeholder, testid, type = "text", disabled }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">{label}</label>
      <input data-testid={testid} type={type} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="mt-1.5 w-full h-11 px-4 rounded-xl border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-[#FF5A36] disabled:bg-zinc-50 disabled:text-zinc-400" />
    </div>
  );
}
